/**
 * Strip flat backdrop plates from building art (grey/black mats from PixFlux).
 * Flood-fill from image edges; pixels matching the sampled backdrop color become transparent.
 */
export const BUILDING_BACKDROP_RGB_TOLERANCE = 28;
/** Corner average — stable when side edges include building shadow greys. */
export function sampleBackdropFromCorners(data, width, height) {
    if (width < 2 || height < 2) {
        return { r: 0, g: 0, b: 0 };
    }
    const idx = (x, y) => (y * width + x) * 4;
    const corners = [
        [0, 0],
        [width - 1, 0],
        [0, height - 1],
        [width - 1, height - 1],
    ];
    let r = 0;
    let g = 0;
    let b = 0;
    for (const [cx, cy] of corners) {
        const i = idx(cx, cy);
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
    }
    return { r: r / 4, g: g / 4, b: b / 4 };
}
/** @deprecated Prefer corner sample for building mats. */
export function sampleBackdropFromRgba(data, width, height) {
    return sampleBackdropFromCorners(data, width, height);
}
/** In-place: edge-connected backdrop pixels → alpha 0. */
export function removeBackdropEdgeFlood(data, width, height, options) {
    if (width < 2 || height < 2)
        return 0;
    const backdrop = options?.backdrop ?? sampleBackdropFromCorners(data, width, height);
    const tol = options?.rgbTolerance ?? BUILDING_BACKDROP_RGB_TOLERANCE;
    const thresh = tol * tol;
    const { r, g, b } = backdrop;
    const skipTop = options?.skipTopEdge !== false;
    const matches = (px) => {
        if (data[px + 3] < 200)
            return false;
        const dr = data[px] - r;
        const dg = data[px + 1] - g;
        const db = data[px + 2] - b;
        return dr * dr + dg * dg + db * db <= thresh;
    };
    const seen = new Uint8Array(width * height);
    const qx = [];
    const qy = [];
    let cleared = 0;
    const tryPush = (x, y) => {
        if (x < 0 || x >= width || y < 0 || y >= height)
            return;
        const p = y * width + x;
        if (seen[p])
            return;
        const px = p * 4;
        if (!matches(px))
            return;
        seen[p] = 1;
        qx.push(x);
        qy.push(y);
    };
    for (let x = 0; x < width; x++) {
        tryPush(x, 0);
        if (!skipTop)
            tryPush(x, height - 1);
    }
    for (let y = 0; y < height; y++) {
        tryPush(0, y);
        tryPush(width - 1, y);
    }
    let head = 0;
    while (head < qx.length) {
        const x = qx[head];
        const y = qy[head];
        head++;
        const px = (y * width + x) * 4;
        if (data[px + 3] > 0)
            cleared++;
        data[px + 3] = 0;
        tryPush(x - 1, y);
        tryPush(x + 1, y);
        tryPush(x, y - 1);
        tryPush(x, y + 1);
    }
    return cleared;
}
